/**
 * PNG metadata embed/extract using XMP (iTXt chunk).
 *
 * PNG structure: 8-byte signature + sequence of chunks.
 * Each chunk: 4-byte length + 4-byte type + data + 4-byte CRC.
 *
 * XMP is stored as an iTXt chunk with keyword "XML:com.adobe.xmp".
 * This is the standard recognized by Microsoft Word, Google Docs,
 * PowerPoint, and other Office applications for alt text / captions.
 *
 * iTXt format: keyword + null + compression flag + compression method
 *              + language tag + null + translated keyword + null + UTF-8 text
 *
 * Backward compatibility: extractPNGMetadata falls back to reading
 * legacy tEXt chunks if no XMP chunk is found.
 */

import type { ExportMetadata } from './types';

const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const XMP_KEYWORD = 'XML:com.adobe.xmp';
const ALCHEMIE_DATA_KEY = 'AlchemieData';
const ALCHEMIE_NS = 'https://alchem.ie/ns/metadata';

// ── CRC32 lookup table (pre-computed) ────────────────────────────────────────

const crcTable = new Uint32Array(256);
for (let n = 0; n < 256; n++) {
  let c = n;
  for (let k = 0; k < 8; k++) {
    c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
  }
  crcTable[n] = c;
}

function crc32(buf: Uint8Array): number {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < buf.length; i++) {
    crc = crcTable[(crc ^ buf[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

// ── Chunk helpers ────────────────────────────────────────────────────────────

interface PNGChunk {
  type: string;
  data: Uint8Array;
}

/** Parse a PNG buffer into its constituent chunks. */
function parseChunks(buf: Uint8Array): PNGChunk[] {
  const chunks: PNGChunk[] = [];
  let offset = 8; // Skip signature

  while (offset < buf.length) {
    const length = (buf[offset] << 24) | (buf[offset + 1] << 16) | (buf[offset + 2] << 8) | buf[offset + 3];
    offset += 4;

    const type = String.fromCharCode(buf[offset], buf[offset + 1], buf[offset + 2], buf[offset + 3]);
    offset += 4;

    const data = buf.slice(offset, offset + length);
    offset += length;

    offset += 4; // Skip CRC

    chunks.push({ type, data });
  }

  return chunks;
}

/** Serialize a chunk into bytes (length + type + data + CRC). */
function serializeChunk(chunk: PNGChunk): Uint8Array {
  const typeBytes = new TextEncoder().encode(chunk.type);
  const length = chunk.data.length;

  // CRC covers type + data
  const crcInput = new Uint8Array(4 + length);
  crcInput.set(typeBytes, 0);
  crcInput.set(chunk.data, 4);
  const crcValue = crc32(crcInput);

  const result = new Uint8Array(4 + 4 + length + 4);
  // Length (big-endian)
  result[0] = (length >>> 24) & 0xFF;
  result[1] = (length >>> 16) & 0xFF;
  result[2] = (length >>> 8) & 0xFF;
  result[3] = length & 0xFF;
  // Type
  result.set(typeBytes, 4);
  // Data
  result.set(chunk.data, 8);
  // CRC (big-endian)
  result[8 + length] = (crcValue >>> 24) & 0xFF;
  result[9 + length] = (crcValue >>> 16) & 0xFF;
  result[10 + length] = (crcValue >>> 8) & 0xFF;
  result[11 + length] = crcValue & 0xFF;

  return result;
}

/** Reassemble chunks into a PNG buffer. */
function assembleChunks(chunks: PNGChunk[]): Uint8Array {
  const parts: Uint8Array[] = [PNG_SIGNATURE];
  for (const chunk of chunks) {
    parts.push(serializeChunk(chunk));
  }
  const totalLength = parts.reduce((sum, p) => sum + p.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

// ── iTXt chunk helpers ──────────────────────────────────────────────────────

/**
 * Create an iTXt chunk.
 * Format: keyword + \0 + compression_flag(0) + compression_method(0)
 *         + language_tag + \0 + translated_keyword + \0 + utf8_text
 */
function createITxtChunk(keyword: string, text: string): PNGChunk {
  const keywordBytes = new TextEncoder().encode(keyword);
  const textBytes = new TextEncoder().encode(text);
  // keyword + null + 0 + 0 + "" + null + "" + null + text
  const data = new Uint8Array(keywordBytes.length + 1 + 2 + 1 + 1 + textBytes.length);
  let offset = 0;
  data.set(keywordBytes, offset); offset += keywordBytes.length;
  data[offset++] = 0; // null separator after keyword
  data[offset++] = 0; // compression flag (uncompressed)
  data[offset++] = 0; // compression method
  // language tag: empty string, terminated by null
  data[offset++] = 0;
  // translated keyword: empty string, terminated by null
  data[offset++] = 0;
  data.set(textBytes, offset);
  return { type: 'iTXt', data };
}

/** Parse an iTXt chunk to extract keyword and text. */
function parseITxtChunk(data: Uint8Array): { keyword: string; text: string } | null {
  // Find keyword (null-terminated)
  const nullIndex = data.indexOf(0);
  if (nullIndex === -1) return null;

  const keyword = new TextDecoder().decode(data.slice(0, nullIndex));

  // After keyword null: compression_flag(1) + compression_method(1) + language(null-term) + translated(null-term) + text
  let offset = nullIndex + 1;
  if (offset + 2 > data.length) return null;

  const compressionFlag = data[offset++];
  offset++; // compression method

  if (compressionFlag !== 0) return null; // We don't support compressed iTXt

  // Skip language tag (null-terminated)
  const langEnd = data.indexOf(0, offset);
  if (langEnd === -1) return null;
  offset = langEnd + 1;

  // Skip translated keyword (null-terminated)
  const transEnd = data.indexOf(0, offset);
  if (transEnd === -1) return null;
  offset = transEnd + 1;

  const text = new TextDecoder().decode(data.slice(offset));
  return { keyword, text };
}

// ── Legacy tEXt chunk helpers (for backward-compatible reading) ─────────────

/** Parse a tEXt chunk into key-value pair. */
function parseTextChunk(data: Uint8Array): { key: string; value: string } {
  const nullIndex = data.indexOf(0);
  if (nullIndex === -1) return { key: '', value: '' };
  const key = new TextDecoder().decode(data.slice(0, nullIndex));
  const value = new TextDecoder().decode(data.slice(nullIndex + 1));
  return { key, value };
}

// ── XMP packet helpers ──────────────────────────────────────────────────────

/** Escape XML special characters. */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Unescape XML entities back to plain text. */
function unescapeXml(text: string): string {
  return text
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&');
}

/** Build an XMP packet from metadata fields. */
function buildXmpPacket(meta: ExportMetadata): string {
  const dcParts: string[] = [];

  if (meta.title) {
    dcParts.push(
      `      <dc:title>`,
      `        <rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(meta.title)}</rdf:li></rdf:Alt>`,
      `      </dc:title>`,
    );
  }

  if (meta.description) {
    dcParts.push(
      `      <dc:description>`,
      `        <rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(meta.description)}</rdf:li></rdf:Alt>`,
      `      </dc:description>`,
    );
  }

  if (meta.author) {
    dcParts.push(
      `      <dc:creator>`,
      `        <rdf:Seq><rdf:li>${escapeXml(meta.author)}</rdf:li></rdf:Seq>`,
      `      </dc:creator>`,
    );
  }

  if (meta.copyright) {
    dcParts.push(
      `      <dc:rights>`,
      `        <rdf:Alt><rdf:li xml:lang="x-default">${escapeXml(meta.copyright)}</rdf:li></rdf:Alt>`,
      `      </dc:rights>`,
    );
  }

  const xmpParts: string[] = [];

  if (meta.software) {
    xmpParts.push(`      <xmp:CreatorTool>${escapeXml(meta.software)}</xmp:CreatorTool>`);
  }

  const alchemieParts: string[] = [];

  if (meta.alchemieData) {
    const encoded = encodeAlchemieData(meta.alchemieData);
    alchemieParts.push(`      <alchemie:data>${escapeXml(encoded)}</alchemie:data>`);
  }

  const nsAttrs = [
    'xmlns:dc="http://purl.org/dc/elements/1.1/"',
    'xmlns:xmp="http://ns.adobe.com/xap/1.0/"',
    `xmlns:alchemie="${ALCHEMIE_NS}"`,
  ].join('\n        ');

  const allParts = [...dcParts, ...xmpParts, ...alchemieParts];

  return [
    `<?xpacket begin="\xEF\xBB\xBF" id="W5M0MpCehiHzreSzNTczkc9d"?>`,
    `<x:xmpmeta xmlns:x="adobe:ns:meta/">`,
    `  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">`,
    `    <rdf:Description rdf:about=""`,
    `        ${nsAttrs}>`,
    ...allParts,
    `    </rdf:Description>`,
    `  </rdf:RDF>`,
    `</x:xmpmeta>`,
    `<?xpacket end="w"?>`,
  ].join('\n');
}

/** Extract a single XMP field value using regex. Handles rdf:Alt/rdf:Seq wrappers. */
function extractXmpField(xmp: string, namespace: string, field: string): string | null {
  // Try rdf:Alt wrapper (dc:title, dc:description, dc:rights)
  const altPattern = new RegExp(
    `<${namespace}:${field}>[\\s\\S]*?<rdf:li[^>]*>([\\s\\S]*?)</rdf:li>[\\s\\S]*?</${namespace}:${field}>`,
    'i'
  );
  const altMatch = xmp.match(altPattern);
  if (altMatch) return unescapeXml(altMatch[1].trim());

  // Try rdf:Seq wrapper (dc:creator)
  const seqPattern = new RegExp(
    `<${namespace}:${field}>[\\s\\S]*?<rdf:li>([\\s\\S]*?)</rdf:li>[\\s\\S]*?</${namespace}:${field}>`,
    'i'
  );
  const seqMatch = xmp.match(seqPattern);
  if (seqMatch) return unescapeXml(seqMatch[1].trim());

  // Try simple element (xmp:CreatorTool, alchemie:data)
  const simplePattern = new RegExp(
    `<${namespace}:${field}[^>]*>([\\s\\S]*?)</${namespace}:${field}>`,
    'i'
  );
  const simpleMatch = xmp.match(simplePattern);
  if (simpleMatch) return unescapeXml(simpleMatch[1].trim());

  return null;
}

/** Parse an XMP packet into ExportMetadata. */
function parseXmpPacket(xmp: string): ExportMetadata {
  const meta: ExportMetadata = {};

  const title = extractXmpField(xmp, 'dc', 'title');
  if (title) meta.title = title;

  const description = extractXmpField(xmp, 'dc', 'description');
  if (description) meta.description = description;

  const creator = extractXmpField(xmp, 'dc', 'creator');
  if (creator) meta.author = creator;

  const rights = extractXmpField(xmp, 'dc', 'rights');
  if (rights) meta.copyright = rights;

  const software = extractXmpField(xmp, 'xmp', 'CreatorTool');
  if (software) meta.software = software;

  const alchemieRaw = extractXmpField(xmp, 'alchemie', 'data');
  if (alchemieRaw) {
    const decoded = decodeAlchemieData(alchemieRaw);
    if (decoded) meta.alchemieData = decoded;
  }

  return meta;
}

// ── Public API ───────────────────────────────────────────────────────────────

const ALCHEMIE_PREFIX = 'alchemie:v1:';

/** Base64 encode that handles Unicode (works in both browser and Node.js). */
function toBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/** Base64 decode that handles Unicode (works in both browser and Node.js). */
function fromBase64(b64: string): string {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/**
 * Encode app-specific data for storage in metadata.
 * Produces a versioned base64 string that is opaque to casual inspection.
 */
export function encodeAlchemieData(data: Record<string, unknown>): string {
  return ALCHEMIE_PREFIX + toBase64(JSON.stringify(data));
}

/**
 * Decode app-specific data from metadata.
 * Handles versioned base64 format (alchemie:v1:...) with fallback to plain JSON
 * for backward compatibility with files exported before obfuscation.
 */
export function decodeAlchemieData(encoded: string): Record<string, unknown> | undefined {
  try {
    if (encoded.startsWith(ALCHEMIE_PREFIX)) {
      const payload = encoded.slice(ALCHEMIE_PREFIX.length);
      return JSON.parse(fromBase64(payload));
    }
    // Fallback: plain JSON (pre-obfuscation files)
    return JSON.parse(encoded);
  } catch {
    return undefined;
  }
}

/**
 * Embed metadata into a PNG file using XMP.
 *
 * Creates an iTXt chunk with keyword "XML:com.adobe.xmp" containing
 * a full XMP packet. This is the format recognized by Microsoft Word,
 * Google Docs, PowerPoint, and other Office applications.
 *
 * Existing XMP iTXt chunks are replaced. Legacy tEXt metadata chunks
 * are also removed to avoid stale duplicates.
 *
 * @param pngBuffer - Raw PNG bytes (from File.arrayBuffer(), fetch, etc.)
 * @param meta - Metadata to embed
 * @returns New PNG buffer with metadata embedded
 */
export function embedPNGMetadata(pngBuffer: Uint8Array, meta: ExportMetadata): Uint8Array {
  const hasContent = meta.title || meta.description || meta.author ||
    meta.software || meta.copyright || meta.alchemieData;
  if (!hasContent) return pngBuffer;

  const chunks = parseChunks(pngBuffer);

  // Remove existing XMP iTXt chunks and legacy tEXt metadata chunks
  const legacyKeys = new Set(['Title', 'Description', 'Author', 'Software', 'Copyright', ALCHEMIE_DATA_KEY]);
  const filtered = chunks.filter(chunk => {
    if (chunk.type === 'iTXt') {
      const parsed = parseITxtChunk(chunk.data);
      return !parsed || parsed.keyword !== XMP_KEYWORD;
    }
    if (chunk.type === 'tEXt') {
      const { key } = parseTextChunk(chunk.data);
      return !legacyKeys.has(key);
    }
    return true;
  });

  // Build XMP packet and create iTXt chunk
  const xmpPacket = buildXmpPacket(meta);
  const xmpChunk = createITxtChunk(XMP_KEYWORD, xmpPacket);

  // Insert before first IDAT
  const idatIndex = filtered.findIndex(c => c.type === 'IDAT');
  const insertAt = idatIndex === -1 ? filtered.length - 1 : idatIndex;

  const result = [
    ...filtered.slice(0, insertAt),
    xmpChunk,
    ...filtered.slice(insertAt),
  ];

  return assembleChunks(result);
}

/**
 * Extract metadata from a PNG file.
 *
 * Reads the XMP iTXt chunk and parses Dublin Core / XMP fields.
 * Falls back to reading legacy tEXt chunks for files exported before
 * the XMP migration.
 *
 * @param pngBuffer - Raw PNG bytes
 * @returns Extracted metadata (empty fields omitted)
 */
export function extractPNGMetadata(pngBuffer: Uint8Array): ExportMetadata {
  const chunks = parseChunks(pngBuffer);

  // Try XMP iTXt chunk first
  for (const chunk of chunks) {
    if (chunk.type === 'iTXt') {
      const parsed = parseITxtChunk(chunk.data);
      if (parsed && parsed.keyword === XMP_KEYWORD) {
        return parseXmpPacket(parsed.text);
      }
    }
  }

  // Fallback: legacy tEXt chunks (pre-XMP files)
  const meta: ExportMetadata = {};
  for (const chunk of chunks) {
    if (chunk.type !== 'tEXt') continue;
    const { key, value } = parseTextChunk(chunk.data);

    switch (key) {
      case 'Title': meta.title = value; break;
      case 'Description': meta.description = value; break;
      case 'Author': meta.author = value; break;
      case 'Software': meta.software = value; break;
      case 'Copyright': meta.copyright = value; break;
      case ALCHEMIE_DATA_KEY: {
        const decoded = decodeAlchemieData(value);
        if (decoded) meta.alchemieData = decoded;
        break;
      }
    }
  }

  return meta;
}
